import axios from "axios";
import { jwtDecode } from "jwt-decode";
import http from "./http";
import httpInstance from "./config";
import AuthService from "./AuthService";
import { OK } from "../utils/constant";

const API_URL =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_API_DEVELOPMENT_URL
    : process.env.REACT_APP_API_PRODUCTION_URL;

function joinApiPath(path) {
  const base = (API_URL || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return `${base}/${p}`;
}

function normalizeToken(raw) {
  if (raw == null) return null;
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  return t || null;
}

function accessTokenNeedsRefresh(token) {
  try {
    const decoded = jwtDecode(token);
    const exp = Math.floor(decoded.exp);
    const now = Math.floor(Date.now() / 1000);
    const proactiveSkewSeconds = 120;
    return exp < now + proactiveSkewSeconds;
  } catch {
    return true;
  }
}

async function refreshPatientAccessToken() {
  const refreshToken = normalizeToken(AuthService.getRefreshToken());
  if (!refreshToken) return;
  try {
    const response = await axios.post(
      joinApiPath("refresh-token"),
      { refresh_token: refreshToken },
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.status === OK) {
      const resData = response.data;
      if (resData.code === OK && resData.data) {
        AuthService.storeToken(resData.data.access_token);
        AuthService.storeRefreshToken(resData.data.refresh_token);
      }
    }
  } catch (e) {
    console.error("UnifiedSessionService: token refresh failed", e);
  }
}

async function bearerHeadersForPatientApi() {
  let access = normalizeToken(AuthService.getToken());
  if (!access) return {};
  if (accessTokenNeedsRefresh(access)) {
    await refreshPatientAccessToken();
    access = normalizeToken(AuthService.getToken());
  }
  if (!access) return {};
  return { Authorization: `Bearer ${access}` };
}

async function authorizedGet(url, params = {}) {
  const run = async () => {
    const headers = await bearerHeadersForPatientApi();
    if (!headers.Authorization) return { kind: "no_auth" };
    const response = await httpInstance.get(url, { params, headers });
    return { kind: "ok", response };
  };
  try {
    const first = await run();
    if (first.kind === "no_auth") return null;
    return first.response;
  } catch (e1) {
    if (e1?.response?.status !== 401) throw e1;
    await refreshPatientAccessToken();
    try {
      const second = await run();
      if (second.kind === "no_auth") return null;
      return second.response;
    } catch (e2) {
      throw e2;
    }
  }
}

async function authorizedPost(url, body) {
  const run = async () => {
    const headers = await bearerHeadersForPatientApi();
    if (!headers.Authorization) return { kind: "no_auth" };
    const response = await httpInstance.post(url, body, { headers });
    return { kind: "ok", response };
  };
  try {
    const first = await run();
    if (first.kind === "no_auth") return null;
    return first.response;
  } catch (e1) {
    if (e1?.response?.status !== 401) throw e1;
    await refreshPatientAccessToken();
    try {
      const second = await run();
      if (second.kind === "no_auth") return null;
      return second.response;
    } catch (e2) {
      throw e2;
    }
  }
}

function normalizeSessionData(data) {
  if (!data || typeof data !== "object") return null;
  const idx = Number.isFinite(Number(data.current_step)) ? Number(data.current_step) : 0;
  return {
    ...data,
    last_completed_step: idx > 0 ? idx - 1 : null,
  };
}

const UnifiedSessionService = {
  getSessionStatus: async (token, options = {}) => {
    try {
      const query =
        options.reentryMode || options.reviewMode ? "?reentry=1" : "";
      const response = await http.GET(`patient-unified-session-status/${token}${query}`);
      if ((response.code === 200 || response.code === 201) && response.data) {
        return response.data;
      }
      return response;
    } catch (error) {
      return null;
    }
  },

  login: async (payload) => {
    try {
      return await http.POSTwithoutAES("patient-unified-login", payload);
    } catch (error) {
      return error?.data || null;
    }
  },

  /**
   * patient.session + Bearer JWT — same axios instance as questionnaires; response is not AES-wrapped (no http.GET).
   */
  getSession: async (token, patientFormId, options = {}) => {
    if (!patientFormId) return null;
    const wantsReentry = !!(options.reentryMode || options.reviewMode);

    const buildFromStatus = async () => {
      const status = await UnifiedSessionService.getSessionStatus(token, {
        reentryMode: wantsReentry,
      });
      if (!status?.step_manifest?.length) return null;
      return {
        code: 200,
        data: normalizeSessionData({
          step_manifest: status.step_manifest,
          current_step: status.current_step ?? 0,
          total_steps: status.total_steps ?? status.step_manifest.length,
          status: status.status,
          reentry_mode: true,
          review_mode: true,
          patient_form_id: status.patient_form_id,
          visit_id: status.visit_id,
          employee_id: status.employee_id,
        }),
      };
    };

    /** Kiosk: staff session — load manifest via public status only (no GET patient-unified-session). */
    if (options.kiosk) {
      return buildFromStatus();
    }

    const params = { patient_form_id: patientFormId };
    if (wantsReentry) {
      params.reentry = 1;
    }
    try {
      const response = await authorizedGet(`patient-unified-session/${token}`, params);
      if (!response) {
        return wantsReentry ? buildFromStatus() : null;
      }
      const body = response?.data;
      if (!body) {
        return wantsReentry ? buildFromStatus() : null;
      }
      if (body.code === 200 && body.data && typeof body.data === "object") {
        const data = normalizeSessionData(body.data);
        if (wantsReentry) {
          data.reentry_mode = true;
          data.review_mode = true;
        }
        const hasManifest = Array.isArray(data.step_manifest) && data.step_manifest.length > 0;
        if (hasManifest || !wantsReentry) {
          return { ...body, data };
        }
        const fallback = await buildFromStatus();
        if (fallback) return fallback;
        return { ...body, data };
      }
      if (wantsReentry) {
        return buildFromStatus();
      }
      return body;
    } catch (error) {
      if (wantsReentry) {
        return buildFromStatus();
      }
      return error?.response?.data || null;
    }
  },

  completeStep: async (token, stepIndex, patientFormId, options = {}) => {
    if (!patientFormId) return null;
    const payload = {
      step_index: stepIndex,
      patient_form_id: patientFormId,
    };
    if (options.reentryMode || options.reviewMode) {
      payload.reentry = 1;
    }
    try {
      const response = await authorizedPost(`patient-unified-step-complete/${token}`, payload);
      if (!response) return null;
      const body = response?.data;
      if (!body) return null;
      if (body.code === 200 && body.data && typeof body.data === "object") {
        return { code: 200, data: normalizeSessionData(body.data) };
      }
      return body;
    } catch (error) {
      return error?.response?.data ?? null;
    }
  },

  unifiedCompleteStep: async (token, stepIndex, _stepName, patientFormId, options = {}) => {
    return UnifiedSessionService.completeStep(token, stepIndex, patientFormId, options);
  },
};

export default UnifiedSessionService;
